import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { uploadFamilyRule, uploadFile } from "../../DAL/create";
import { GetFamilyRuleById } from "../../DAL/fetch";
import { UpdateFamilyRule } from "../../DAL/update";

function CreateFamilyRule() {
  const navigate = useNavigate();
  const params = useParams();
  const [formType, setFormType] = useState("Add");

  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileUploaded, setFileUploaded] = useState(false);
  const [entriesEffectId, setEntriesEffectId] = useState("");
  const [message, setMessage] = useState(false);

  const [level, setLevel] = useState("");
  const [score, setScore] = useState("");
  const [frameForOwner, setFrameForOwner] = useState(true);
  const [notificationAllBroad, setNotificationAllBroad] = useState(false);
  const [familyTopScore, setFamilyTopScore] = useState(true);
  const [frameForTopScorers, setFrameForTopScorers] = useState();
  const [freeVoiceNoteForFamilyOwner, setFreeVoiceNoteForFamilyOwner] =
    useState();
  const [
    publicBulletMessageForFamilyOwner,
    setPublicBulletMessageForFamilyOwner,
  ] = useState();
  const [bulletMessageForFamilyOwner, setBulletMessageForFamilyOwner] =
    useState();

  const parseBoolean = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "string") {
      return val.toLowerCase() === "yes" || val.toLowerCase() === "true";
    }
    return false;
  };

  const fetchRuleDetail = useCallback(async () => {
    const resp = await GetFamilyRuleById(params?.id);
    if (resp?.status === 200) {
      setFormType("Update");
      const data = resp.data.data;
      setLevel(data.level);
      setScore(data.score);
      setFrameForOwner(parseBoolean(data.frameForOwner));
      setNotificationAllBroad(parseBoolean(data.notificationAllBroad));
      setFamilyTopScore(parseBoolean(data.familyTopScore));
      setEntriesEffectId(data.entriesEffectSvg);
      setFrameForTopScorers(data.frameForTopScorers);
      setFreeVoiceNoteForFamilyOwner(data.freeVoiceNoteForFamilyOwner);
      setPublicBulletMessageForFamilyOwner(
        data.publicBulletMessageForFamilyOwner
      );
      setBulletMessageForFamilyOwner(data.bulletMessageForFamilyOwner);
    } else {
      enqueueSnackbar(resp?.message || "Failed to fetch rule", {
        variant: "error",
      });
    }
  }, [params?.id]);

  useEffect(() => {
    if (params?.id) {
      fetchRuleDetail();
    }
  }, [params?.id, fetchRuleDetail]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : "");
    setFileUploaded(false);
  };

  const handleFileUpload = async () => {
    if (!file) {
      setMessage(true);
      return;
    }
    const formData = new FormData();
    formData.append("image", file);

    setLoading(true);
    try {
      const res = await uploadFile(formData);
      if (res?.data?.id) {
        setEntriesEffectId(res.data.url);
        setFileUploaded(true);
        enqueueSnackbar("Image uploaded successfully!", { variant: "success" });
      } else {
        throw new Error("Invalid image upload response");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      enqueueSnackbar("Failed to upload image", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // if (!entriesEffectId) {
    //   setMessage(true);
    //   return;
    // }

    const payload = {
      level,
      score,
      frameForOwner: String(frameForOwner),
      notificationAllBroad: String(notificationAllBroad),
      familyTopScore: String(familyTopScore),
      entriesEffectSvg: entriesEffectId,
      frameForTopScorers,
      freeVoiceNoteForFamilyOwner,
      publicBulletMessageForFamilyOwner,
      bulletMessageForFamilyOwner,
    };

    setLoading(true);
    try {
      let res;
      if (formType === "Add") {
        res = await uploadFamilyRule(payload);
      } else {
        res = await UpdateFamilyRule(params?.id, payload);
      }
      if (res?.status === 200 || res?.status === 201) {
        enqueueSnackbar(
          formType === "Add"
            ? "Rule created successfully!"
            : "Rule updated successfully!",
          { variant: "success" }
        );
        navigate(-1);
      } else {
        enqueueSnackbar(
          res?.message || res?.error || res?.data?.message || res?.data?.error,
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error saving rule:", error);
      enqueueSnackbar("Failed to save family rule", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", p: 3 }}>
      <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <ArrowBackIcon
          sx={{ cursor: "pointer", mb: 1 }}
          onClick={() => navigate(-1)}
        />
        <Typography variant="h5" gutterBottom>
          {formType === "Add" ? "Create Family Rule" : "Update Family Rule"}
        </Typography>
      </Box>

      <TextField
        label="Level"
        fullWidth
        value={level}
        onChange={(e) => setLevel(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Score"
        fullWidth
        value={score}
        onChange={(e) => setScore(e.target.value)}
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={frameForOwner}
            onChange={(e) => setFrameForOwner(e.target.checked)}
          />
        }
        label="Frame For Owner"
      />
      <FormControlLabel
        control={
          <Switch
            checked={notificationAllBroad}
            onChange={(e) => setNotificationAllBroad(e.target.checked)}
          />
        }
        label="Notification All Broad"
      />
      <FormControlLabel
        control={
          <Switch
            checked={familyTopScore}
            onChange={(e) => setFamilyTopScore(e.target.checked)}
          />
        }
        label="Family Top Score"
      />

      <Typography variant="h6" sx={{ mt: 2 }}>
        Upload Entries Effect SVG
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <input type="file" accept="image/*" onChange={handleFileChange} />
        <Button
          variant="contained"
          color={fileUploaded ? "success" : "primary"}
          onClick={handleFileUpload}
          disabled={loading || !file}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : fileUploaded ? (
            "Uploaded"
          ) : (
            "Upload"
          )}
        </Button>
      </Box>

      <TextField
        label="Frame For Top Scorers"
        type="number"
        fullWidth
        sx={{ mt: 2 }}
        value={frameForTopScorers}
        onChange={(e) => setFrameForTopScorers(Number(e.target.value))}
      />
      <TextField
        label="Free Voice Note For Family Owner"
        type="number"
        fullWidth
        sx={{ mt: 2 }}
        value={freeVoiceNoteForFamilyOwner}
        onChange={(e) => setFreeVoiceNoteForFamilyOwner(Number(e.target.value))}
      />
      <TextField
        label="Public Bullet Message For Family Owner"
        type="number"
        fullWidth
        sx={{ mt: 2 }}
        value={publicBulletMessageForFamilyOwner}
        onChange={(e) =>
          setPublicBulletMessageForFamilyOwner(Number(e.target.value))
        }
      />
      <TextField
        label="Bullet Message For Family Owner"
        type="number"
        fullWidth
        sx={{ mt: 2 }}
        value={bulletMessageForFamilyOwner}
        onChange={(e) => setBulletMessageForFamilyOwner(Number(e.target.value))}
      />

      <Button
        variant="contained"
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit}
        // disabled={loading}
      >
        {formType === "Add" ? "Create Rule" : "Update Rule"}
      </Button>
    </Box>
  );
}

export default CreateFamilyRule;
